<template>
  <div class="sd-map-control full-height full-width">
    <div id="map-container" ref="mapControl" class="full-height full-width" style="width: 100%"></div>
    <div class="map-component">
      <slot></slot>
    </div>
  </div>
</template>

<script>
import Map from 'ol/Map'
import View from 'ol/View'
import { defaults } from 'ol/control'
import { DragPan, defaults as defaultInteraction } from 'ol/interaction'
import ProjectionUtil from './Utils/ProjectionUtil'
/**
 * @description 2d基础地图组件
 * @author GuoYun
 */
export default {
  name: 'SdMap',
  data() {
    return {
      map_: null,
      listeners_: null,
      viewParameters_: {
        projection: 'EPSG:3857',
        // center: [112.976835, 28.194235], //初始化中心
        zoom: 12, //初始化级别
        maxZoom: 18, //最大级别
        minZoom: 1, //最小级别
      },
      interactions_: {},
      baseLayers_: {},
      layers_: {},
    }
  },
  props: {
    viewProps: {
      type: Object,
      default() {
        return {}
      },
    },
    interactions: {
      typeof: Object,
      default() {
        return {}
      },
    },
  },
  created() {
    this.initParameter()
    ProjectionUtil.init()
  },
  mounted() {
    this.initMap()
  },
  methods: {
    initParameter() {
      this.viewParameters_ = Object.assign(this.viewParameters_, this.viewProps)
      this.interactions_ = Object.assign(
        {
          dragPan: false,
          doubleClickZoom: false,
        },
        this.interactions
      )
    },
    initMap() {
      var dragPan = new DragPan({
        condition: this.dragCondition,
      })
      this.map_ = new Map({
        target: this.$refs.mapControl,
        view: new View(this.viewParameters_),
        interactions: defaultInteraction(this.interactions_).extend([dragPan]),
        controls: defaults(),
      })

      this.initComponents()
      this.$emit('get-map', this.map_, this)
    },
    initComponents() {
      const children = this.$children
      children.forEach((child) => {
        if (child.setMap) {
          try {
            child.setMap(this.map_, this)
          } catch (error) {
            console.error(error)
          }
        }
      })
    },

    /**
     * 添加图层 ，并为图层指定名称，，isBase为True添加成底图
     * @param {string} name
     * @param {Layer} layer
     * @param {boolean} isBase
     * @param {boolean} isAdd 是否追加数据
     * @param {boolean} trigger 是否触发添加事件
     */
    addLayer(name, layer, isBase = false, isAdd = false, trigger = true) {
      layer.set('name', name)
      var addLayersObj = isBase ? this.baseLayers_ : this.layers_
      var addLayers = addLayersObj[name] || (addLayersObj[name] = [])
      if (!isAdd) {
        this.removeLayerByName(name, isBase, false)
        addLayers = []
      }
      addLayers.push(layer)
      addLayersObj[name] = addLayers
      this.map_.addLayer(layer)
      if (trigger) {
        this.dispatchEvent('addLayer', { name, layer, isBase, isAdd })
      }
    },

    /**
     * 通过图层名称移除地图中图层列表，isBase为True移除底图图层
     * @param {string} name
     * @param {boolean} isBase
     * @param {boolean} trigger 是否触发移除事件
     */
    removeLayerByName(name, isBase = false, trigger = true) {
      if (!this.map_) {
        return
      }
      const layers = isBase ? this.baseLayers_[name] : this.layers_[name]
      if (layers) {
        layers.forEach((layer) => {
          this.map_.removeLayer(layer)
        })
      }
      if (trigger) {
        this.dispatchEvent('removeLayer', { name, layers })
      }

      return layers
    },
    /**
     * 通过图层名称更新地图中图层列表，isBase为True移除底图图层
     * @param {string} name
     * @param {boolean} layers
     * @param {boolean} isBase
     */
    updateLayerByName(name, layers, isBase) {
      var addLayersObj = isBase ? this.baseLayers_ : this.layers_
      var addLayers = addLayersObj[name]
      if (addLayers) {
        addLayersObj[name] = Array.isArray(layers) ? layers : [layers]
      }
    },
    /**
     * 通过名称修改地图底图显示
     * @param {string} name
     */
    changeBaseLayerVisible(name,showLabel=true) {
      var setLayers = this.baseLayers_
      Object.keys(setLayers).forEach((lyrName) => {
        var visible = lyrName === name
        setLayers[lyrName].forEach((layer) => {
          const label = layer.get('isLabel')
          if (label) {
            layer.setVisible(showLabel && visible)
          }else{
            layer.setVisible(visible)
          }
          
        })
      })
      this.dispatchEvent('changeBaseLayerVisible', { name })
    },
    /**
     * 通过名称修改地图图层显示
     * @param {string} name
     * @param {boolean} visible
     */
    setLayerVisibleByName(name, visible) {
      var setLayers = this.layers_[name]
      if (setLayers) {
        setLayers.forEach((layer) => {
          layer.setVisible(visible)
        })
      }
      this.dispatchEvent('setLayerVisible', { name, visible, layer: setLayers })
    },

    /**
     * 通过图层名称获得地图中图层列表，isBase为True返回底图图层
     * @param {string} name
     * @param {boolean} isBase
     */
    getLayerByName(name, isBase = false) {
      return isBase ? this.baseLayers_[name] : this.layers_[name]
    },
    /**
     * 获得地图中图层列表，isBase为True返回底图列表
     * @param {boolean} isBase
     */
    getLayerList(isBase = false) {
      return isBase ? this.baseLayers_ : this.layers_
    },
    /**
     * 添加控件的监听事件
     * @param {string} type Type.
     * @param {Event.Listener} listener Listener.
     */
    addEventListener(type, listener) {
      if (!type || !listener) {
        return
      }
      const listeners = this.listeners_ || (this.listeners_ = {})
      const listenersForType = listeners[type] || (listeners[type] = [])
      if (!listenersForType.includes(listener)) {
        listenersForType.push(listener)
      }
    },
    /**
     * 移除控件的监听事件
     * @param {string} type Type.
     * @param {Event.Listener} listener Listener.
     */
    removeEventListener(type, listener) {
      const listeners = this.listeners_ && this.listeners_[type]
      if (listeners) {
        const index = listeners.indexOf(listener)
        if (index !== -1) {
          listeners.splice(index, 1)
          if (listeners.length === 0) {
            delete this.listeners_[type]
          }
        }
      }
    },
    /**
     * 激活指定事件
     * @param {string} event 事件类型.
     * @param {object} params 传递事件参数.
     */
    dispatchEvent(event, params) {
      const isString = typeof event === 'string'
      const type = isString ? event : event.type
      const listeners = this.listeners_ && this.listeners_[type]
      if (!listeners) {
        return
      }
      listeners.forEach((listener) => {
        try {
          listener(params)
        } catch (error) {
          console.error(event, ':', error)
        }
      })
    },

    /**
     * 拖拽地图条件
     * @param {String} event 事件类型.
     */
    dragCondition(event) {
      return event.originalEvent.button < 2
    },
    /**
     * 注册坐标系信息
     * @param {String | Number} code EPSG code
     * @param {String} prj4def Proj4 格式坐标系信息.
     */
    addProjection(code, prj4def) {
      ProjectionUtil.register(code, prj4def)
    },
    /**
     * 获得所有坐标系信息
     */
    getProjections() {
      ProjectionUtil.getProjections()
    },
  },
}
</script>

<style scoped>
/*
  Map地图组件样式
  @component SdMap
*/
.sd-map-control {
  position: relative;
}
</style>
